import { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Copy, Download, Printer } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface QrCodeCardProps {
  /** The runner URL the QR encodes. */
  url: string;
  /** The activation short code (shown beneath the QR). */
  shortCode: string;
  title?: string;
}

/**
 * Renders the activation's QR code (encoding the participant runner URL) plus copy / download /
 * print affordances so the operator can hand it out or pin it to a kiosk.
 */
export function QrCodeCard({ url, shortCode, title = 'Runner QR code' }: QrCodeCardProps) {
  const { toast } = useToast();
  const canvasWrapRef = useRef<HTMLDivElement>(null);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: 'Copied', description: 'Runner URL copied to clipboard.' });
    } catch {
      toast({ title: 'Copy failed', description: url, variant: 'destructive' });
    }
  };

  const download = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `activation-${shortCode}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const print = () => {
    const canvas = canvasWrapRef.current?.querySelector('canvas');
    if (!canvas) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(
      `<html><head><title>${shortCode}</title></head><body style="text-align:center;font-family:sans-serif;">` +
        `<img src="${canvas.toDataURL('image/png')}" style="width:320px;height:320px"/>` +
        `<p style="font-size:24px;letter-spacing:2px">${shortCode}</p>` +
        `<p>${url}</p></body></html>`,
    );
    win.document.close();
    win.print();
  };

  return (
    <Card className="flex flex-col items-center gap-3 p-4" data-testid="qr-code-card">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <div ref={canvasWrapRef} className="rounded-lg bg-white p-3">
        <QRCodeCanvas value={url} size={180} data-testid="qr-canvas" />
      </div>
      <p className="font-mono text-lg tracking-widest text-foreground" data-testid="qr-short-code">
        {shortCode}
      </p>
      <p className="max-w-full truncate text-xs text-muted-foreground" title={url}>
        {url}
      </p>
      <div className="flex flex-wrap justify-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copyUrl} data-testid="qr-copy">
          <Copy className="mr-2 h-4 w-4" />
          Copy URL
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={download} data-testid="qr-download">
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={print} data-testid="qr-print">
          <Printer className="mr-2 h-4 w-4" />
          Print
        </Button>
      </div>
    </Card>
  );
}
